<template>
  <aside class="w-80 h-full min-h-0 bg-[#fbfcfc] border-r border-[#1c4848]/10 flex flex-col shrink-0 relative z-40 hidden md:flex">
    <ExtendedSidebarHeader title="Pages">
      <template #actions>
        <Dropdown align="right" width="200px">
          <button
            type="button"
            class="p-1.5 hover:bg-[#1c4848]/5 rounded-lg text-[#1c4848]/60 transition-colors"
            aria-label="More options"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>
          </button>
          <template #items>
            <DropdownItemGroup>
              <DropdownItem as="button" @click="enableChecking">Select for publish</DropdownItem>
              <DropdownItem as="button" @click="publishBook">Publish this book</DropdownItem>
            </DropdownItemGroup>
          </template>
        </Dropdown>
      </template>
      <ExtendedSidebarSearch v-model="searchQuery" placeholder="Search pages..." />
    </ExtendedSidebarHeader>

    <ExtendedSidebarAddButton label="Add New Page" @click="openCreatePageModal" />

    <div class="flex-1 overflow-y-auto no-scrollbar px-4 pb-6 space-y-3 min-h-0">
      <ExtendedSidebarPageCard
        v-for="(page, i) in filteredPages"
        :key="page.id"
        :page="page"
        :index="i + 1"
        :book-id="bookId"
        :is-active="currentPageId === String(page.id)"
        @publish="publishPage"
        @edit="openEditPageModal"
        @delete="openDeleteConfirm"
      />
    </div>

    <ConfirmDialog
      v-model="showDeleteConfirm"
      title="Obriši stranu"
      :message="deleteConfirmMessage"
      confirm-label="Obriši"
      cancel-label="Odustani"
      variant="danger"
      @confirm="confirmDeletePage"
    />
  </aside>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useModal } from 'vue-final-modal';
import { usePagesStore } from '@/store/pages';
import { useContentFeedStore } from '@/store/content-feed';
import { useBooksStore } from '@/store/books';
import { getContentFeedType, getContentLanguageId } from '@/utils/contentFeedType.js';
import ExtendedSidebarHeader from '@/components/app/library/extended-sidebar/ExtendedSidebarHeader.vue';
import ExtendedSidebarSearch from '@/components/app/library/extended-sidebar/ExtendedSidebarSearch.vue';
import ExtendedSidebarAddButton from '@/components/app/library/extended-sidebar/ExtendedSidebarAddButton.vue';
import ExtendedSidebarPageCard from '@/components/app/library/extended-sidebar/ExtendedSidebarPageCard.vue';
import CreatePage from '@/components/app/dashboard/modals/CreatePage.vue';
import EditPage from '@/components/app/dashboard/modals/EditPage.vue';
import ConfirmDialog from '@/components/app/dashboard/modals/ConfirmDialog.vue';
import Dropdown from '@/components/app/dropdown/Dropdown.vue';
import DropdownItem from '@/components/app/dropdown/DropdownItem.vue';
import DropdownItemGroup from '@/components/app/dropdown/DropdownItemGroup.vue';

const route = useRoute();
const router = useRouter();
const pagesStore = usePagesStore();
const contentFeedStore = useContentFeedStore();
const booksStore = useBooksStore();

const searchQuery = ref('');
const showDeleteConfirm = ref(false);
const pageToDelete = ref(null);

const deleteConfirmMessage = computed(() => {
  const p = pageToDelete.value;
  const title = p?.title ?? p?.name ?? 'Ova strana';
  return `Da li ste sigurni da želite da obrišete "${title}"? Ova akcija se ne može poništiti.`;
});

const bookId = computed(() => route.params.bookId);
const currentPageId = computed(() => route.params.pageId ?? null);

const filteredPages = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return pagesStore.pages;
  return pagesStore.pages.filter((p) => {
    const title = (p.title ?? p.name ?? '').toLowerCase();
    const desc = (p.description ?? p.content ?? '').toLowerCase();
    return title.includes(q) || desc.includes(q);
  });
});

function fetchPages() {
  if (bookId.value) pagesStore.fetchPages(bookId.value);
}

onMounted(fetchPages);
watch(bookId, (id) => { if (id) fetchPages(); });

function openCreatePageModal() {
  const { open, close } = useModal({
    component: CreatePage,
    attrs: {
      data: {},
      onConfirm() {
        fetchPages();
        close();
      }
    }
  });
  open();
}

function openEditPageModal(page) {
  if (!page?.id) return;
  const { open, close } = useModal({
    component: EditPage,
    attrs: {
      page,
      onConfirm() {
        fetchPages();
        close();
      }
    }
  });
  open();
}

function enableChecking() {
  pagesStore.checkingEnabled = !pagesStore.checkingEnabled;
}

const currentBook = computed(() => {
  const id = bookId.value;
  if (!id) return null;
  return (booksStore.books ?? []).find((b) => String(b.id) === String(id)) ?? null;
});

function publishBook() {
  const book = currentBook.value;
  const title = book?.name ?? book?.title ?? 'Nova knjiga';
  const type = getContentFeedType(book ?? { id: bookId.value }, 'book');
  const languageId = getContentLanguageId(book);
  contentFeedStore.createContentFeed({ title, type, language_id: languageId }).then((response) => {
    booksStore.attachContentFeedToBook(bookId.value, response.id);
  });
}

function publishPage(page) {
  const book = currentBook.value;
  const pageWithBook = { ...page, book_audience: book?.audience, book: book ? { audience: book.audience } : null };
  const title = page?.title ?? page?.name ?? 'Page';
  const type = getContentFeedType(pageWithBook, 'page');
  const languageId = getContentLanguageId(page, book);
  contentFeedStore.createContentFeed({ title, type, language_id: languageId }).then((response) => {
    pagesStore.attachContentFeedToPage(page.id, response.id);
  });
}

function openDeleteConfirm(page) {
  pageToDelete.value = page;
  showDeleteConfirm.value = true;
}

async function confirmDeletePage() {
  const page = pageToDelete.value;
  if (!page?.id) return;
  const deletedId = page.id;
  const wasActive = currentPageId.value === String(deletedId);
  const remaining = pagesStore.pages.filter((p) => p.id !== deletedId);
  try {
    await pagesStore.deletePage(deletedId);
    pageToDelete.value = null;
    if (wasActive && bookId.value) {
      if (remaining.length > 0) {
        router.replace({ path: `/books/${bookId.value}/pages/${remaining[0].id}` });
      } else {
        router.replace({ path: `/books/${bookId.value}` });
      }
    }
  } catch (err) {
    console.error(err);
  }
}
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.no-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.no-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(28, 72, 72, 0.15);
  border-radius: 3px;
}
</style>
